import type { DaaLevel } from '@/lib/mqtt/schemas/daaAlerts';
/**
 * DAA Alerts tab of the right panel (ENG-340) — driven by the `daa_alerts`
 * topic via `useDaaAlertsStore`. "Active" is one row per ownship/intruder pair
 * (the latest message wins); "Recent" is the log of encounters starting,
 * escalating and ending, which is what the tab's unread counter counts.
 */
import { type DaaEncounter, type DaaLogEntry, useDaaAlertsStore } from '@/lib/store/daaAlerts';
import { cn } from '@/lib/utils';

export const DAA_LEVEL_LABEL: Record<DaaLevel, string> = {
  0: 'Clear',
  1: 'Advisory',
  2: 'Caution',
  3: 'Warning',
};

const levelTone = (l: DaaLevel) => (l >= 3 ? 'crit' : l === 2 ? 'warn' : 'info');

const fmtRange = (m: number | null) => {
  if (m == null) return null;
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};

const detail = (e: { rangeM: number | null; verticalSepM: number | null }) =>
  [fmtRange(e.rangeM), e.verticalSepM != null ? `↕${Math.round(e.verticalSepM)} m` : null]
    .filter(Boolean)
    .join(' ');

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function Dot({ tone }: { tone: 'crit' | 'warn' | 'info' | 'muted' }) {
  return (
    <span
      className={cn(
        'w-2 h-2 rounded-full mt-1 shrink-0',
        tone === 'crit' && 'bg-(--color-red)',
        tone === 'warn' && 'bg-(--color-amber)',
        tone === 'info' && 'bg-(--color-blue)',
        tone === 'muted' && 'bg-(--color-text3)',
      )}
    />
  );
}

function ActiveRow({ e }: { e: DaaEncounter }) {
  const resolved = e.status === 'resolved';
  const tone = resolved ? 'muted' : levelTone(e.level);
  return (
    <div
      className={cn(
        'grid grid-cols-[10px_1fr] gap-1.5 py-2 border-b border-(--color-border-lite) last:border-b-0',
        resolved && 'opacity-60',
      )}
    >
      <Dot tone={tone} />
      <div className="min-w-0">
        <span
          className={cn(
            'text-[0.846em] font-bold leading-tight font-sans',
            tone === 'crit' && 'text-(--color-red)',
            tone === 'warn' && 'text-(--color-amber)',
            tone === 'info' && 'text-(--color-blue)',
            tone === 'muted' && 'text-(--color-text2)',
          )}
        >
          {resolved ? 'Resolved' : DAA_LEVEL_LABEL[e.level]} — {e.intruderLabel}
        </span>
        <div className="text-[0.692em] font-mono text-(--color-text2) mt-0.5">{detail(e)}</div>
        {e.advisory && !resolved && (
          <div className="text-[0.692em] text-(--color-text2) leading-snug mt-0.5">
            {e.advisory}
          </div>
        )}
      </div>
    </div>
  );
}

function LogRow({ e }: { e: DaaLogEntry }) {
  const verb = e.kind === 'resolved' ? 'Cleared' : DAA_LEVEL_LABEL[e.level];
  const tone = e.kind === 'resolved' ? 'muted' : levelTone(e.level);
  return (
    <div className="grid grid-cols-[52px_10px_1fr] gap-1.5 py-1.5 border-b border-(--color-border-lite) last:border-b-0 items-start">
      <span className="text-[0.692em] font-mono text-(--color-text3) pt-0.5">{clock(e.at)}</span>
      <Dot tone={tone} />
      <div className="min-w-0 text-[0.692em] text-(--color-text2) leading-snug">
        <span className="font-bold">{verb}</span> — {e.intruderLabel}
        {detail(e) && <span className="font-mono"> · {detail(e)}</span>}
      </div>
    </div>
  );
}

export function DaaAlertsPane() {
  const byKey = useDaaAlertsStore((s) => s.byKey);
  const log = useDaaAlertsStore((s) => s.log);

  const active = Object.values(byKey).sort(
    (a, b) =>
      Number(a.status === 'resolved') - Number(b.status === 'resolved') ||
      b.level - a.level ||
      (a.rangeM ?? Number.POSITIVE_INFINITY) - (b.rangeM ?? Number.POSITIVE_INFINITY),
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto px-3 pt-1.5">
      {active.length === 0 && log.length === 0 && (
        <div className="text-[0.769em] text-(--color-text3) text-center py-6">No DAA alerts.</div>
      )}
      {active.length > 0 && (
        <section aria-label="Active DAA alerts">
          <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) py-1">
            Active
          </div>
          {active.map((e) => (
            <ActiveRow key={e.key} e={e} />
          ))}
        </section>
      )}
      {log.length > 0 && (
        <section aria-label="Recent DAA alerts" className="mt-2">
          <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) py-1">
            Recent
          </div>
          {log.map((e) => (
            <LogRow key={e.id} e={e} />
          ))}
        </section>
      )}
    </div>
  );
}
